
import { createApp, defineComponent, toRaw } from 'vue';
import { GoogleMapLoaderService } from '@/shared/services/GoogleMapLoaderService';
import { getMapId, hasLabelsEnabled, mapSettings } from '@/shared/constants/mapSettings';
import { mapCountries } from '@/shared/constants/mapCountries';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import MapControls from '@/shared/components/MapControls.vue';
import AdInfoWindow from '@/ad/components/AdInfoWindow.vue';
import { createPopupClass } from '@/shared/models/Popup';
import { formatNumber, formatCurrency, formatDate, formatCash, formatTimeAgo } from '@/shared/filters';
import { i18n } from '@/i18n';
import { Country } from '../models/Country';
import config from '@/shared/constants/config';
import { mapStyles, mapStylesWithLabels } from '../constants/mapStyles';

export default defineComponent({
  name: 'MapSearch',
  emits: ['boundsChanged', 'selected', 'rerender'],
  components: {
    MapControls,
  },
  props: {
    country: {
      type: String,
      default: 'sk',
    },
    ads: {
      type: Array,
      default: [] as any[],
    },
    polylines: {
      type: Array,
      default: [] as any[],
    },
    selected: {
      type: Object,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    bounds: {
      type: Object,
    },
    zoom: {
      type: Number,
    },
    isVisible: {
      type: Boolean,
    },
    gestureHandling: {
      type: String,
    },
    mapId: {
      type: String,
    },
  },
  data() {
    return {
      map: null as null | google.maps.Map,
      place: null as null,
      countryLayer: null as null,
      admin1Layer: null as null,
      admin2Layer: null as null,
      localityLayer: null as null,
      postalCodeLayer: null as null,
      allLayers: null as null,
      markers: [] as google.maps.Marker[],
      mapPolylines: [] as google.maps.Polyline[],
      markerClusterer: null as null | MarkerClusterer,
      selectedMarker: null as null | google.maps.Marker,
      infoWindows: [] as any[],
      boundsInitialized: false, //required when map is initialized in fullscreen
      isFilterByPlace: false,
      boundsChangedTimeout: null as any,
      isLabelsEnabled: false,
    };
  },
  watch: {
    ads: function () {
      this.createMarkers();
    },
    polylines: function () {
      this.createPolylines();
    },
    selected: function (item) {
      if (item) {
        this.addSelectedMarker(item);
      } else {
        this.removeSelectedMarker();
      }
    },
    isVisible: function () {
      this.handleBounds();
    },
  },
  async mounted() {
    await GoogleMapLoaderService.load();
    this.initializeMap();
  },
  computed: {
    mapConfig() {
      const options = {
        ...mapSettings,
      } as google.maps.MapOptions;
      if (config.IS_DATA_DRIVEN_STYLING_ENABLED) {
        options.styles = null;
        if (this.mapId) {
          options.mapId = this.mapId;
        } else {
          options.mapId = getMapId(this.isLabelsEnabled);
        }
      }
      if (this.gestureHandling) {
        options.gestureHandling = this.gestureHandling;
      }
      if (this.bounds) {
        if (this.zoom) {
          options.zoom = this.zoom;
        }
      } else if (this.countryInfo) {
        options.center = this.countryInfo.center;
        //ptions.zoom = this.countryInfo.zoom;
      }
      return options;
    },
    countryInfo(): Country {
      return this.country ? mapCountries[this.country as string] : null;
    },
    isDataDrivenStylingAvailable() {
      return this.map.getMapCapabilities && this.map.getMapCapabilities().isDataDrivenStylingAvailable;
    },
  },
  methods: {
    initializeMap() {
      if(!window.google || !window.google.maps) {
        return;
      }
      const mapContainer = this.$refs.googleMap as HTMLElement;
      let options = this.mapConfig;
      this.isLabelsEnabled = hasLabelsEnabled(options.mapId);
      this.map = new window.google.maps.Map(mapContainer, options);
      this.initLayers();
      this.handleBounds();
      this.map.addListener('drag', () => {
        this.isFilterByPlace = false;
      });
      this.map.addListener('zoom_changed', () => {
        this.isFilterByPlace = false;
      });
      this.map.addListener('idle', () => {
        if (this.boundsChangedTimeout) clearTimeout(this.boundsChangedTimeout);
        this.boundsChangedTimeout = setTimeout(() => {
          if (this.map) {
            const bounds = this.map.getBounds();
            if (bounds) {
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              const sameBounds = ne.lat() == sw.lat() && ne.lng() == sw.lng();
              if (!sameBounds) {
                this.boundsInitialized = true;
                if (this.isFilterByPlace) {
                  this.$emit('boundsChanged', bounds, true);
                } else {
                  this.$emit('boundsChanged', bounds, false);
                }
                //this.isFilterByPlace = false;
              }
            }
          }
        }, 500);
      });
    },
    initLayers() {
      if (!this.isDataDrivenStylingAvailable) return;

      let rawMap = toRaw(this.map);
      this.countryLayer = rawMap.getFeatureLayer(google.maps.FeatureType.COUNTRY);
      this.admin1Layer = rawMap.getFeatureLayer(google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_1);
      this.admin2Layer = rawMap.getFeatureLayer(google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_2);
      this.localityLayer = rawMap.getFeatureLayer(google.maps.FeatureType.LOCALITY);
      this.postalCodeLayer = rawMap.getFeatureLayer(google.maps.FeatureType.POSTAL_CODE);
      this.allLayers = [this.countryLayer, this.admin1Layer, this.admin2Layer, this.localityLayer, this.postalCodeLayer];
      this.applyStyle();
    },
    applyStyle() {
      if (!this.isDataDrivenStylingAvailable) return;

      let styleStrokeFill = {
        strokeColor: '#da487f',
        strokeOpacity: 1.0,
        strokeWeight: 2.0,
        fillColor: '#da487f',
        fillOpacity: 0.1,
      };

      this.revertStyles();

      const featureStyle = (params) => {
        if (this.place && params.feature.placeId == this.place.place_id) {
          return styleStrokeFill;
        } else {
          return null;
        }
      };

      if (this.place && this.place.types && this.place.types.length > 0) {
        switch (this.place.types[0]) {
          case 'country':
            toRaw(this.countryLayer).style = featureStyle;
            break;
          case 'administrative_area_level_1':
            toRaw(this.admin1Layer).style = featureStyle;
            break;
          case 'administrative_area_level_2':
            toRaw(this.admin2Layer).style = featureStyle;
            break;
          case 'locality':
            toRaw(this.localityLayer).style = featureStyle;
            break;
          case 'postal_code':
            toRaw(this.postalCodeLayer).style = featureStyle;
            break;
          default:
            break;
        }
      }
    },
    revertStyles() {
      for (const layer of this.allLayers) {
        toRaw(layer).style = null;
      }
    },
    handleBounds() {
      if (this.boundsInitialized) {
        return;
      }
      if (this.map) {
        if (this.bounds && this.bounds.swlat && this.bounds.swlng && this.bounds.nelat && this.bounds.nelng) {
          this.map.fitBounds(
            new window.google.maps.LatLngBounds({ lat: this.bounds.swlat, lng: this.bounds.swlng }, { lat: this.bounds.nelat, lng: this.bounds.nelng }),
            0,
          );
        } else if (this.countryInfo) {
          const bounds = new window.google.maps.LatLngBounds(
            { lat: this.countryInfo.sw_lat, lng: this.countryInfo.sw_lng },
            { lat: this.countryInfo.ne_lat, lng: this.countryInfo.ne_lng },
          );
          this.map.fitBounds(bounds);
        }
      }
    },
    createMarker(item: any, customClass?: string): null | google.maps.Marker {
      if (this.map) {
        let markerText = '';
        let markerClass = '' as string | undefined;
        let adGuid = '' as string | undefined;
        const isCluster = item.count > 1;
        const isMultipleOnSameLocation = item.items && item.items.length;

        if (isMultipleOnSameLocation) {
          markerText = this.$t('MapSearch.more-ads', { count: item.items.length });
        } else if (isCluster) {
          markerText = item.count;
          markerClass = `map-cluster-icon map-cluster-icon-${this.getClusterClass(item.count, 3)}`;
          adGuid = undefined;
        } else {
          if (item.price) {
            let decimalPlaces = item.adType === 1 ? 1 : 0;
            if (item.price < 10000) {
              decimalPlaces = 2;
            }
            markerText = formatCash(item.price, decimalPlaces);

            if (item.estateType === 500) {
              markerText = this.$t('price.from') + ' ' + markerText;
            }
          } else {
            if (item.estateAgencyId) {
              markerText = this.$t('price.empty-agency');
            } else {
              markerText = this.$t('price.empty-marker');
            }
          }
          markerClass = `map-marker-${item.adType} map-marker-estate-${item.estateType} ${customClass ? customClass : ''}`;
          adGuid = item.adGuid;
        }

        const marker = new window.google.maps.Marker({
          position: { lat: item.y, lng: item.x },
          map: this.map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 0,
          },
          label: {
            text: markerText,
            fontFamily: markerClass, //class added to label
          },
        });

        if (isMultipleOnSameLocation) {
          marker.set(
            'adGuids',
            item.items.map((x: any) => x.adGuid),
          );
        } else if (adGuid) {
          marker.set('adGuid', adGuid);
        }

        const markerLabel = (marker as any).markerLabel;

        if (markerLabel) {
          if (this.selectedMarker && this.selectedMarker.get('selected') === 'true') {
            const adGuid = this.selectedMarker.get('adGuid');
            const adGuids = this.selectedMarker.get('adGuids');
            let isSelected = false;
            if (isMultipleOnSameLocation) {
              isSelected = adGuids && JSON.stringify(adGuids) == JSON.stringify(item.items.map((x: any) => x.adGuid));
            } else if (adGuid) {
              isSelected = adGuid && item.adGuid === adGuid;
            }
            if (isSelected) {
              this.selectedMarker.setMap(null);
              this.selectedMarker = null;
              this.selectedMarker = marker;
              marker.set('selected', 'true');
              markerLabel.className += ' map-marker-selected';
            }
          }

          markerLabel.addLabelListener('mouseleave', () => {
            if (isCluster) return;

            for (const popup of this.infoWindows) {
              popup.setMap(null);
            }
            this.infoWindows = [];
          });

          markerLabel.addLabelListener('mouseover', () => {
            if (isCluster) return;

            for (const popup of this.infoWindows) {
              popup.setMap(null);
            }
            this.infoWindows = [];
            const popup = this.getPopup(item);
            this.infoWindows.push(popup);
            if (this.map) {
              popup.setMap(this.map);
            }
          });

          markerLabel.addLabelListener('click', () => {
            if (isCluster && !isMultipleOnSameLocation) {
              if (this.map && item.bounds) {
                const bounds = new window.google.maps.LatLngBounds(
                  { lat: item.bounds.minY, lng: item.bounds.minX },
                  { lat: item.bounds.maxY, lng: item.bounds.maxX },
                );
                this.map.fitBounds(bounds);
              }
            } else {
              for (const popup of this.infoWindows) {
                popup.setMap(null);
              }
              this.infoWindows = [];
              if (this.isMobile || isMultipleOnSameLocation) {
                const popup = this.getPopup(item);
                const position = new window.google.maps.LatLng(item.y, item.x);
                this.infoWindows.push(popup);
                if (this.map) {
                  popup.setMap(this.map);
                  if (this.isMobile) {
                    this.map.panTo(position);
                  }
                }
              } else {
                this.$emit('selected', item);
              }
            }
          });
        }

        return marker;
      }
      return null;
    },
    getPopup(item) {
      const InfoWindow = defineComponent({
        extends: AdInfoWindow,
      });

      const instance = createApp(InfoWindow, {
        ad: item,
      });

      instance.use(i18n);

      instance.config.globalProperties.$filters = {
        number: formatNumber,
        currency: formatCurrency,
        date: formatDate,
        cash: formatCash,
        timeAgo: formatTimeAgo,
      };

      const wrapper = document.createElement('div');
      instance.mount(wrapper);

      const Popup = createPopupClass();
      const position = new window.google.maps.LatLng(item.y, item.x);
      const popup = new Popup(position, wrapper);
      return popup;
    },
    getClusterClass(count: number, numStyles: number) {
      let index = 0;

      let dv = count;
      while (dv !== 0) {
        dv = Math.floor(dv / 10);
        index++;
      }

      index = Math.min(index, numStyles);

      return index;
    },
    addMarker(item: any) {
      const marker = this.createMarker(item);
      if (marker) {
        this.markers.push(marker);
      }
    },
    addSelectedMarker(item: any) {
      this.removeSelectedMarker();
      if (this.map) {
        if (this.markers.length > 0) {
          let markerFound = this.markers.filter((x) => x.get('adGuid') === item.adGuid);
          if (markerFound.length === 0) {
            markerFound = this.markers.filter((x) => x.get('adGuids') && x.get('adGuids').indexOf(item.adGuid) > -1);
          }
          if (markerFound.length) {
            this.selectedMarker = markerFound[0];
            this.selectedMarker.set('selected', 'true');
            (this.selectedMarker as any).markerLabel.className += ' map-marker-selected';
            this.redrawMarker(this.selectedMarker);
            return;
          }
        }
        this.selectedMarker = this.createMarker(item, 'map-marker-selected');
      }
    },
    redrawMarker(marker: google.maps.Marker) {
      marker.setMap(null);
      marker.setMap(this.map);
    },
    removeSelectedMarker() {
      if (this.selectedMarker) {
        const marker = this.selectedMarker as any;
        if (marker.get('selected') === 'true') {
          const adGuid = this.selectedMarker.get('adGuid');
          const adGuids = this.selectedMarker.get('adGuids');
          let isFound = false;
          if (adGuid) {
            isFound = this.markers.filter((x) => x.get('adGuid') === adGuid).length > 0;
          } else if (adGuids) {
            const adGuidsString = JSON.stringify(adGuids);
            isFound = this.markers.filter((x) => x.get('adGuids') && JSON.stringify(x.get('adGuids')) === adGuidsString).length > 0;
          }
          if (isFound) {
            const classes = marker.markerLabel.className;
            marker.markerLabel.className = classes.replace(' map-marker-selected', '');
            this.redrawMarker(this.selectedMarker);
            return;
          }
        }
        this.selectedMarker.setMap(null);
        this.selectedMarker = null;
      }
    },
    setMapOnAll(map: null | google.maps.Map) {
      for (let i = 0; i < this.markers.length; i++) {
        this.markers[i].setMap(map);
      }
    },
    createMarkers() {
      this.deleteMarkers();
      for (const item of this.ads) {
        this.addMarker(item);
      }
      if (this.markerClusterer) {
        this.markerClusterer.addMarkers(this.markers);
      }
    },
    clearMarkers() {
      this.setMapOnAll(null);
    },
    showMarkers() {
      this.setMapOnAll(this.map);
    },
    deleteMarkers() {
      this.clearMarkers();
      this.markers = [];
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers();
      }
    },
    createPolylines() {
      this.deletePolylines();
      if (this.polylines) {
        for (const item of this.polylines) {
          this.addPolyline(item);
        }
      }
    },
    addPolyline(item: any) {
      const polyline = this.createPolyline(item);
      if (polyline) {
        this.mapPolylines.push(polyline);
      }
    },
    createPolyline(item: any): null | google.maps.Polyline {
      if (this.map) {
        const polyline = new window.google.maps.Polyline({
          path: [new window.google.maps.LatLng(item.y, item.x, false), new window.google.maps.LatLng(item.y2, item.x2, false)],
          strokeColor: '#f00',
          strokeOpacity: 1, //0.7,
          strokeWeight: 2,
          map: this.map,
        });
        return polyline;
      }
      return null;
    },
    clearPolylines() {
      for (let i = 0; i < this.mapPolylines.length; i++) {
        this.mapPolylines[i].setMap(null);
      }
    },
    deletePolylines() {
      this.clearPolylines();
      this.mapPolylines = [];
    },
    getZoom() {
      if (this.map) {
        return this.map.getZoom();
      }
      return null;
    },
    canUnzoom() {
      return this.map && this.mapConfig && this.map.getZoom() > (this.mapConfig as any).minZoom;
    },
    zoomIn() {
      if (this.map) {
        this.map.setZoom(this.map.getZoom() + 1);
      }
    },
    unzoom() {
      if (this.map) {
        this.map.setZoom(this.map.getZoom() - 1);
      }
    },
    fitBounds(markers: any[]) {
      const bounds = new window.google.maps.LatLngBounds();
      if (markers.length > 0) {
        for (let i = 0; i < markers.length; i++) {
          let marker = markers[i];
          if (marker.bounds) {
            bounds.extend({ lat: marker.bounds.maxY, lng: marker.bounds.maxX });
            bounds.extend({ lat: marker.bounds.minY, lng: marker.bounds.minX });
          } else {
            bounds.extend({ lat: marker.y, lng: marker.x });
          }
        }
        if (this.map) {
          this.map.fitBounds(bounds);
        }
      }
    },
    filterByPlace(place: null | google.maps.places.PlaceResult) {
      if (place && place.geometry && this.map) {
        this.place = place;
        this.applyStyle();
        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport, 0);
          this.zoomIn();
          if (place.geometry.location) {
            this.map.setCenter(place.geometry.location);
          }
        } else {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(12);
        }
        this.isFilterByPlace = true;
      }
    },
    setCountry(countryCode: string) {
      const country = mapCountries[countryCode];
      if (!country) return;
      const bounds = new window.google.maps.LatLngBounds({ lat: country.sw_lat, lng: country.sw_lng }, { lat: country.ne_lat, lng: country.ne_lng });
      this.map.fitBounds(bounds);
    },
    onShowLabels() {
      this.isLabelsEnabled = !this.isLabelsEnabled;
      if (config.IS_DATA_DRIVEN_STYLING_ENABLED) {
        let mapId = getMapId(this.isLabelsEnabled);
        this.$emit('rerender', mapId);
      } else {
        if (this.isLabelsEnabled) {
          this.map.setOptions({ styles: mapStylesWithLabels } as any);
        } else {
          this.map.setOptions({ styles: mapStyles } as any);
        }
      }
    },
  },
});
